import type { Icon } from '@phosphor-icons/react';
import {
  FolderPlusIcon,
  KeyIcon,
  ListChecksIcon,
  MegaphoneIcon,
  SparkleIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react';
import type { Page } from '@regia/shared';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { lazyNamed } from './common';
import { SchedaInfo } from './pages/SchedaInfo';
import { SchedaOverview } from './pages/SchedaOverview';

export interface SchedaTabProps {
  page: Page;
}

export interface SchedaTab {
  /** Segmento dell'URL dopo /schede/:slug ('' per la prima). */
  id: string;
  label: string;
  element: ComponentType<SchedaTabProps>;
}

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: Icon;
  /** Dove portare; riceve lo slug della scheda aperta, se c'è. */
  to: (pageSlug?: string) => string;
}

export interface HomeSection {
  id: string;
  order: number;
  element: ComponentType;
}

/** Tab della scheda, nell'ordine della specifica 4.2. */
export const schedaTabs: SchedaTab[] = [
  { id: '', label: 'Panoramica', element: SchedaOverview },
  {
    id: 'ai',
    label: 'Agente',
    element: lazyNamed<SchedaTabProps>(() => import('./agents'), 'SchedaAgent'),
  },
  {
    id: 'browser',
    label: 'Browser',
    element: lazyNamed<SchedaTabProps>(() => import('./browser'), 'SchedaBrowser'),
  },
  {
    id: 'contenuti',
    label: 'Contenuti',
    element: lazyNamed<SchedaTabProps>(() => import('./media'), 'SchedaMedia'),
  },
  {
    id: 'piano',
    label: 'Piano',
    element: lazyNamed<SchedaTabProps>(() => import('./posts'), 'SchedaPosts'),
  },
  {
    id: 'attivita',
    label: 'Attività',
    element: lazyNamed<SchedaTabProps>(() => import('./tasks'), 'SchedaTasks'),
  },
  {
    id: 'numeri',
    label: 'Numeri',
    element: lazyNamed<SchedaTabProps>(() => import('./metrics'), 'SchedaMetrics'),
  },
  {
    id: 'accessi',
    label: 'Accessi',
    element: lazyNamed<SchedaTabProps>(() => import('./vault'), 'SchedaVault'),
  },
  { id: 'info', label: 'Info', element: SchedaInfo },
];

const withPage = (base: string, slug?: string) =>
  slug ? `${base}${base.includes('?') ? '&' : '?'}scheda=${slug}` : base;

/** Azioni del pulsante "+" (spec 6.3). */
export const quickActions: QuickAction[] = [
  {
    id: 'carica',
    label: 'Carica foto o video',
    description: 'Dal rullino o dalla fotocamera',
    icon: UploadSimpleIcon,
    to: (slug) => withPage('/carica', slug),
  },
  {
    id: 'post',
    label: 'Nuovo post',
    description: 'Prepara un post per una scheda',
    icon: MegaphoneIcon,
    to: (slug) => withPage('/agenda/nuovo-post', slug),
  },
  {
    id: 'attivita',
    label: 'Nuova attività',
    description: 'Una cosa da fare, con promemoria',
    icon: ListChecksIcon,
    to: (slug) => withPage('/attivita?nuova=1', slug),
  },
  {
    id: 'accesso',
    label: 'Nuovo accesso',
    description: 'Salva una password nella cassaforte',
    icon: KeyIcon,
    to: (slug) => withPage('/accessi?nuovo=1', slug),
  },
  {
    id: 'scheda',
    label: 'Nuova scheda',
    description: 'Una pagina o un cliente',
    icon: FolderPlusIcon,
    to: () => '/schede?nuova=1',
  },
  {
    id: 'ai',
    label: 'Chiedi all’AI',
    description: 'Domande, controlli, bozze',
    icon: SparkleIcon,
    to: (slug) => withPage('/ai', slug),
  },
];

/** Sezioni aggiuntive della pagina Oggi fornite dai moduli. */
export const homeSections: HomeSection[] = [];

/** Pagine principali dei moduli (figlie del layout con accesso). */
export const moduleRoutes: RouteObject[] = [
  { path: 'carica', Component: lazyNamed(() => import('./media'), 'UploadPage') },
  { path: 'agenda/*', Component: lazyNamed(() => import('./agenda'), 'AgendaPage') },
  { path: 'attivita', Component: lazyNamed(() => import('./tasks'), 'TasksPage') },
  { path: 'accessi', Component: lazyNamed(() => import('./vault'), 'VaultPage') },
  { path: 'ai', Component: lazyNamed(() => import('./assistant'), 'AssistantPage') },
  { path: 'agenti', Component: lazyNamed(() => import('./agents'), 'AgentsPage') },
  { path: 'chat', Component: lazyNamed(() => import('./chat'), 'ChatListPage') },
  { path: 'chat/:slug', Component: lazyNamed(() => import('./chat'), 'AgentChatPage') },
  { path: 'impostazioni/*', Component: lazyNamed(() => import('./settings'), 'SettingsPage') },
];
